import { useIdentity, withAuthenticationRequired } from '@equipmentshare/auth0-react'
import { ApolloProvider } from '@apollo/client'
import { BrowserRouter } from 'react-router-dom'

import LinearProgress from '@mui/material/LinearProgress'
import { HelmetProvider } from '@equipmentshare/ds2-helmet'

import AppHeader from '../components/AppHeader'
import AppShell from './components/AppShell'
import ActionModal from '../components/ActionModal'
import Toasts from '../components/Toasts'
import IndexRoutes from './index.routes'

import { ModalProvider } from '../contexts/modal-context/modal-context'
import { ToastProvider } from '../contexts/toasts/toast-context'
import getApolloClient from '../config/apollo-client'
import { useAuthenticationDetails } from '../hooks/useAuthenticationDetails'
import PermissionsCheck from 'components/PermissionsCheck'
import { Permission } from '../graphql'
import AccessDenied from 'components/AccessDenied/AccessDenied'
import { FunctionComponent } from 'react'
import useShowError from 'hooks/useShowError'
import HeaperComponent from './components/HeaperComponent'
import { useDDRum } from '../datadog/use-dd-rum'

// This nesting is needed so that the ToastProvider and ModalProvider have an established context that can be used for the apolloClient
export const NestedApp: FunctionComponent = () => {
	const { accessToken } = useAuthenticationDetails()
	const showError = useShowError()
	const apolloClient = getApolloClient( accessToken, showError )
	const identity = useIdentity()
	useDDRum()

	return (
		<ApolloProvider client={apolloClient}>
			<HeaperComponent userId={identity.userId} />
			<PermissionsCheck
				permissionsToCheck={[ Permission.CanViewInventory ]}
				renderLoading={() => <LinearProgress />}
				renderNoAccess={() => <AccessDenied />}
			>
				<HelmetProvider appName="Inventory" faviconPath="/favicon.png">
					<BrowserRouter>
						<AppHeader />
						<AppShell>
							<IndexRoutes />
						</AppShell>
						<ActionModal />
						<Toasts />
					</BrowserRouter>
				</HelmetProvider>
			</PermissionsCheck>
		</ApolloProvider>
	)
}

export const App: FunctionComponent = () => {
	return (
		<ToastProvider>
			<ModalProvider>
				<NestedApp />
			</ModalProvider>
		</ToastProvider>
	)
}

export default withAuthenticationRequired( App )
